import { useRef, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Edit, Information, Warning } from '@mint-lib/icons';
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from '@mint-lib/tw-components';

import type {
  ChannelCatalogItem,
  GranularityLevel,
  PlatformsDataContainer,
  TableDataFileHeader,
  ValidatedCell,
} from '../../api/types.js';
import { getCellErrorMessage } from '../../constants/errorsMapping.js';
import { useCellValueRenderer } from '../../hooks/index.js';
import { RichTooltip } from '../atoms/RichTooltip.jsx';
import { DynamicField } from './DynamicField.jsx';
import { TableBodyCell } from './TableComponents.jsx';

type TableBodyValidatedCellProps = {
  formatType: TableDataFileHeader['formatType'];
  granularityLevel?: GranularityLevel;
  cell: ValidatedCell;
  platformCell: ValidatedCell;
  channelCell?: ValidatedCell;
  mediaRowCell?: ValidatedCell;
  platformsByName: PlatformsDataContainer['platformsByName'];
  channelsByName: Record<string, ChannelCatalogItem>;
  mediaRowNamesById?: Record<string, string>;
  handleEditField: (value: string, label?: string) => void;
  isDisabled?: boolean;
};

export const TableBodyValidatedCell = ({
  formatType,
  granularityLevel,
  cell,
  platformCell,
  channelCell,
  mediaRowCell,
  platformsByName,
  channelsByName,
  mediaRowNamesById,
  handleEditField,
  isDisabled,
}: TableBodyValidatedCellProps) => {
  const { t } = useTranslation('@myn/import-hub');

  const errorMessage = getCellErrorMessage({
    t,
    cell,
    formatType,
    granularityLevel,
  });

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const defaultBgColor = cell.isEdited ? 'editable' : 'default';
  const bgColor = cell.error ? 'error' : defaultBgColor;

  const renderCellValue = useCellValueRenderer({
    formatType,
    platformsByName,
    channelsByName,
    mediaRowNamesById,
    error: !!cell.error,
  });

  const valueRef = useRef('');

  const handleChange = (v: string, displayValue?: string) => {
    valueRef.current = v;

    const isSelectableValue = [
      'platform',
      'channel',
      'media_row',
      'date',
    ].includes(formatType);

    if (isSelectableValue) {
      submitFieldValue(displayValue);
      setIsPopoverOpen(false);
    }
  };

  const submitFieldValue = (displayValue?: string) => {
    if (valueRef.current) {
      handleEditField(valueRef.current, displayValue);
    }
  };

  const handlePopoverOpenChange = (open: boolean) => {
    if (isDisabled) {
      return;
    }

    if (!open) {
      submitFieldValue();
    }

    setIsPopoverOpen(open);
  };

  if (cell.isApiManaged) {
    return (
      <TableBodyCell bgColor="default" className="cursor-default">
        <div className="flex w-full min-w-0 items-center">
          <span className="text-text-secondary italic">{t('API-managed')}</span>
        </div>
      </TableBodyCell>
    );
  }

  return (
    <Popover
      open={isPopoverOpen}
      onOpenChange={handlePopoverOpenChange}
      modal={true}
    >
      <PopoverTrigger asChild>
        <TableBodyCell
          bgColor={bgColor}
          className={isDisabled ? 'cursor-default' : 'cursor-pointer'}
        >
          <div className="group flex w-full min-w-0 items-center justify-between gap-1">
            <div className="min-w-0 flex-1">
              {renderCellValue(cell.rawCell)}
            </div>
            {!isDisabled && (
              <Edit className="hidden size-4 shrink-0 fill-tertiary-main group-hover:inline-block" />
            )}
            {cell.error && (
              <RichTooltip description={errorMessage}>
                <div className="inline-flex shrink-0">
                  <Warning className="size-4 fill-text-error" />
                </div>
              </RichTooltip>
            )}
            {cell.isEdited && (
              <RichTooltip description={t('This cell has been edited')}>
                <div className="inline-flex shrink-0">
                  <Information className="size-4 fill-icon-secondary" />
                </div>
              </RichTooltip>
            )}
          </div>
        </TableBodyCell>
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent
          side="bottom"
          align="center"
          sideOffset={0}
          className="z-popover -mt-(--radix-popper-anchor-height) h-(--radix-popper-anchor-height) w-(--radix-popper-anchor-width) min-w-[10rem] overflow-visible"
          data-testid="dynamic-field-popover"
        >
          <DynamicField
            cell={cell}
            formatType={formatType}
            platformCell={platformCell}
            channelCell={channelCell}
            mediaRowCell={mediaRowCell}
            onChange={handleChange}
          />
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
};
